import { Mail, Phone, MapPin, Globe } from "lucide-react";

const splitSkills = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);

export const ResumePreview = ({ data = {}, template = "modern" }) => {
  const d = data;
  const skills = splitSkills(d.skills);
  const exp = d.experience || [];
  const edu = d.education || [];

  const Contact = () => (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
      {d.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{d.email}</span>}
      {d.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{d.phone}</span>}
      {d.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{d.location}</span>}
      {d.website && <span className="inline-flex items-center gap-1"><Globe className="h-3 w-3" />{d.website}</span>}
    </div>
  );

  const Section = ({ title, children }) => (
    <div className="mb-4">
      <h3 className={`text-[12px] font-bold uppercase tracking-wider mb-1.5 ${template === "modern" ? "text-blue-700" : "text-neutral-900"} ${template !== "minimal" ? "border-b border-neutral-300 pb-1" : ""}`}>
        {title}
      </h3>
      {children}
    </div>
  );

  const ExperienceList = () => (
    <>
      {exp.map((e, i) => (
        <div key={i} className="mb-2.5">
          <div className="flex justify-between items-baseline">
            <p className="text-[12px] font-semibold">{e.role || "Role"}{e.company ? ` · ${e.company}` : ""}</p>
            <span className="text-[10px] text-neutral-500">{e.start} {e.end ? `– ${e.end}` : ""}</span>
          </div>
          {(e.bullets || "").split("\n").filter(Boolean).map((b, j) => (
            <p key={j} className="text-[11px] text-neutral-700 leading-snug pl-3 relative before:content-['•'] before:absolute before:left-0">
              {b.replace(/^[-•]\s*/, "")}
            </p>
          ))}
        </div>
      ))}
    </>
  );

  const EducationList = () => (
    <>
      {edu.map((e, i) => (
        <div key={i} className="mb-1.5 flex justify-between items-baseline">
          <p className="text-[12px]"><span className="font-semibold">{e.degree || "Degree"}</span>{e.school ? `, ${e.school}` : ""}</p>
          <span className="text-[10px] text-neutral-500">{e.start} {e.end ? `– ${e.end}` : ""}</span>
        </div>
      ))}
    </>
  );

  const SkillsList = () => (
    <div className="flex flex-wrap gap-1.5">
      {skills.map((s, i) => (
        <span key={i} className={`text-[10px] px-2 py-0.5 rounded ${template === "modern" ? "bg-blue-50 text-blue-700" : "bg-neutral-100 text-neutral-700"}`}>{s}</span>
      ))}
    </div>
  );

  return (
    <div className="bg-white text-neutral-900 w-full aspect-[1/1.414] shadow-2xl overflow-hidden font-body" data-testid="resume-preview">
      <div className="p-8 h-full overflow-hidden">
        {/* Header */}
        {template === "modern" ? (
          <div className="mb-4 pb-3 border-b-2 border-blue-600">
            <h1 className="text-2xl font-heading font-bold tracking-tight">{d.fullName || "Your Name"}</h1>
            <p className="text-[13px] text-blue-700 font-medium mb-1.5">{d.jobTitle || "Professional Title"}</p>
            <Contact />
          </div>
        ) : template === "classic" ? (
          <div className="mb-4 text-center">
            <h1 className="text-2xl font-heading font-bold uppercase tracking-widest">{d.fullName || "Your Name"}</h1>
            <p className="text-[13px] text-neutral-600 mb-1.5">{d.jobTitle || "Professional Title"}</p>
            <div className="flex justify-center"><Contact /></div>
            <div className="border-b border-neutral-800 mt-3" />
          </div>
        ) : (
          <div className="mb-5">
            <h1 className="text-3xl font-heading font-light tracking-tight">{d.fullName || "Your Name"}</h1>
            <p className="text-[13px] text-neutral-500 mb-1.5">{d.jobTitle || "Professional Title"}</p>
            <Contact />
          </div>
        )}

        {d.summary && (
          <Section title="Summary">
            <p className="text-[11px] text-neutral-700 leading-relaxed">{d.summary}</p>
          </Section>
        )}
        {exp.length > 0 && <Section title="Experience"><ExperienceList /></Section>}
        {edu.length > 0 && <Section title="Education"><EducationList /></Section>}
        {skills.length > 0 && <Section title="Skills"><SkillsList /></Section>}
      </div>
    </div>
  );
};
